type DailyQuoteCache = {
  date: string; // YYYY-MM-DD (local)
  quote: string;
};

export const HOME_QUOTES_DAILY_STORAGE_KEY = 'home-quote:daily:v1';
export const HOME_QUOTES_POOL_STORAGE_KEY = 'home-quote:pool:v1';

function getTodayLocalDateKey(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function normalizeQuote(input: string): string {
  return String(input ?? '')
    .replace(/\s+/g, ' ')
    .trim();
}

function safeReadCache(storageKey: string): DailyQuoteCache | null {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<DailyQuoteCache>;
    if (!parsed || typeof parsed !== 'object') return null;
    if (typeof parsed.date !== 'string' || typeof parsed.quote !== 'string') return null;
    return { date: parsed.date, quote: parsed.quote };
  } catch {
    return null;
  }
}

function safeWriteCache(storageKey: string, cache: DailyQuoteCache) {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(cache));
  } catch {
    // ignore storage failures (private mode, quota, etc.)
  }
}

export function getDailyRandomQuote(
  quotes: string[],
  storageKey = HOME_QUOTES_DAILY_STORAGE_KEY
): string {
  if (!Array.isArray(quotes) || quotes.length === 0) return '';

  // In non-browser contexts, just return the first quote.
  if (typeof window === 'undefined' || !window?.localStorage) {
    return quotes[0] ?? '';
  }

  const today = getTodayLocalDateKey();
  const cache = safeReadCache(storageKey);
  if (cache?.date === today && typeof cache.quote === 'string' && quotes.includes(cache.quote)) {
    return cache.quote;
  }

  const picked = quotes[Math.floor(Math.random() * quotes.length)] ?? quotes[0] ?? '';
  safeWriteCache(storageKey, { date: today, quote: picked });
  return picked;
}

function safeReadStringArray(storageKey: string): string[] {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x) => typeof x === 'string').map(normalizeQuote).filter(Boolean);
  } catch {
    return [];
  }
}

function safeWriteStringArray(storageKey: string, values: string[]) {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(values));
  } catch {
    // ignore storage failures (private mode, quota, etc.)
  }
}

/**
 * Reads user-managed quotes from localStorage.
 * These are appended to the default pool (HOME_QUOTES).
 */
export function readCustomHomeQuotes(storageKey = HOME_QUOTES_POOL_STORAGE_KEY): string[] {
  if (typeof window === 'undefined' || !window?.localStorage) return [];
  const raw = safeReadStringArray(storageKey);
  // de-dupe, preserve order
  const seen = new Set<string>();
  const out: string[] = [];
  for (const q of raw) {
    const key = q.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(q);
  }
  return out;
}

export function writeCustomHomeQuotes(quotes: string[], storageKey = HOME_QUOTES_POOL_STORAGE_KEY) {
  if (typeof window === 'undefined' || !window?.localStorage) return;
  const normalized = (Array.isArray(quotes) ? quotes : [])
    .map(normalizeQuote)
    .filter(Boolean);
  // de-dupe, preserve order
  const seen = new Set<string>();
  const out: string[] = [];
  for (const q of normalized) {
    const key = q.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(q);
  }
  safeWriteStringArray(storageKey, out);
}

/**
 * Returns the full quote pool used by the home page:
 * defaults + user-managed custom quotes (deduped).
 */
export function getHomeQuotePool(defaultQuotes: string[], storageKey = HOME_QUOTES_POOL_STORAGE_KEY): string[] {
  const base = (Array.isArray(defaultQuotes) ? defaultQuotes : []).map(normalizeQuote).filter(Boolean);
  const custom = readCustomHomeQuotes(storageKey);

  const seen = new Set<string>();
  const out: string[] = [];
  for (const q of [...base, ...custom]) {
    const key = q.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(q);
  }
  return out;
}

